import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { getEvidenceById } from '../lib/evidence';
import { PAGES } from '../lib/site';

const page = PAGES.find((p) => p.path === '/escolaridade');
const brucki = getEvidenceById('brucki-2003');
const lourenco = getEvidenceById('lourenco-veras-2006');

const ANSWER =
  `A escolaridade altera o desempenho esperado no Mini Mental. No estudo brasileiro de Brucki et al. (2003), as medianas variaram de ${brucki.results.educationMedians.noFormalEducation} pontos em pessoas sem escolaridade formal a ${brucki.results.educationMedians.years12OrMore} pontos entre quem tem 12 anos ou mais de estudo. Essas medianas descrevem grupos e não são pontos de corte diagnósticos.`;

const MEDIANS = [
  { group: 'Sem escolaridade formal', value: brucki.results.educationMedians.noFormalEducation },
  { group: '1 a 4 anos', value: brucki.results.educationMedians.years1To4 },
  { group: '5 a 8 anos', value: brucki.results.educationMedians.years5To8 },
  { group: '9 a 11 anos', value: brucki.results.educationMedians.years9To11 },
  { group: '12 anos ou mais', value: brucki.results.educationMedians.years12OrMore }
];

const REFERENCES = [brucki, lourenco].map((study) => `${study.citation} DOI: ${study.primarySource.value}.`);

const RELATED = [
  {
    to: '/pontos-de-corte',
    label: 'Pontos de corte do Mini Mental no Brasil',
    note: 'compare o que cada estudo realmente mediu e por que os números não são intercambiáveis.'
  },
  {
    to: '/mini-mental-ou-moca',
    label: 'Mini Mental ou MoCA',
    note: 'o efeito teto em alta escolaridade é uma das razões para considerar outro instrumento.'
  },
  {
    to: '/quem-pode-aplicar',
    label: 'Quem pode aplicar o Mini Mental',
    note: 'a padronização da aplicação é o que torna a comparação com referências possível.'
  },
  {
    to: '/acompanhamento-e-queda-de-pontos',
    label: 'Quantos pontos de queda são significativos no acompanhamento',
    note: 'o efeito teto discutido acima também afeta como ler mudanças de pontuação ao longo do tempo.'
  }
];

function pct(value) {
  return `${String(value).replace('.', ',')}%`;
}

export default function Escolaridade() {
  const semEscolaridade = lourenco.results.groups.noFormalEducation;
  const comEscolaridade = lourenco.results.groups.formalEducation;

  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="medianas-brucki">
        <h2 id="medianas-brucki" className="text-2xl mb-5">Quais são as medianas por escolaridade? Brucki et al. (2003)</h2>
        <p className="text-[var(--text-muted)] mb-6">
          O estudo avaliou {brucki.sampleSize} indivíduos saudáveis sem queixas de memória e descreveu o desempenho por escolaridade. A leitura correta destes números é descritiva: eles dizem como o grupo pontuou, não onde começa a doença.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse border border-[var(--border)] rounded-xl overflow-hidden">
            <thead className="bg-[var(--surface-soft)]">
              <tr>
                <th className="p-4 border border-[var(--border)] font-bold">Escolaridade</th>
                <th className="p-4 border border-[var(--border)] font-bold text-right">Mediana observada</th>
              </tr>
            </thead>
            <tbody>
              {MEDIANS.map((row) => (
                <tr key={row.group}>
                  <td className="p-4 border border-[var(--border)]">{row.group}</td>
                  <td className="p-4 border border-[var(--border)] text-right font-bold">{String(row.value).replace('.', ',')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="mediana-nao-e-corte">
        <h2 id="mediana-nao-e-corte" className="text-2xl mb-5">Mediana é ponto de corte?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A mediana é o valor que divide uma distribuição ao meio. Estar abaixo da mediana observada em uma amostra saudável não equivale, por si só, a ter comprometimento cognitivo.
        </p>
        <p className="text-[var(--text-muted)]">
          Um ponto de corte de rastreio é definido por outro procedimento e precisa ser lido junto com a população, o desfecho e o desempenho psicométrico do estudo. Por isso, medianas de Brucki e cortes de estudos de acurácia respondem perguntas diferentes.
        </p>
      </section>

      <section aria-labelledby="lourenco-veras">
        <h2 id="lourenco-veras" className="text-2xl mb-5">Qual é a referência de rastreio de Lourenço e Veras (2006)?</h2>
        <p className="text-[var(--text-muted)] mb-6">
          Este estudo avaliou {lourenco.sampleSize} pessoas com mais de 65 anos atendidas em contexto ambulatorial e mediu explicitamente sensibilidade e especificidade de diferentes divisões de rastreio.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded-2xl">
            <h3 className="font-bold text-[var(--primary)] mb-2">Sem escolaridade formal</h3>
            <p className="text-sm text-[var(--text-muted)]">
              Divisão {semEscolaridade.cutoffPair}: sensibilidade {pct(semEscolaridade.sensitivity)} e especificidade {pct(semEscolaridade.specificity)}.
            </p>
          </div>
          <div className="p-5 bg-[var(--surface)] border border-[var(--border)] rounded-2xl">
            <h3 className="font-bold text-[var(--primary)] mb-2">Com instrução escolar</h3>
            <p className="text-sm text-[var(--text-muted)]">
              Divisão {comEscolaridade.cutoffPair}: sensibilidade {pct(comEscolaridade.sensitivity)} e especificidade {pct(comEscolaridade.specificity)}.
            </p>
          </div>
        </div>
        <p className="text-[var(--text-muted)] mt-6">
          Esses valores pertencem à população e ao padrão de referência daquele estudo. Um resultado acima ou abaixo da divisão não substitui história clínica, avaliação funcional nem investigação etiológica.
        </p>
      </section>

      <section aria-labelledby="por-que-escolaridade-pesa">
        <h2 id="por-que-escolaridade-pesa" className="text-2xl mb-5">Por que a escolaridade pesa tanto no Mini Mental?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O próprio desempenho do MEEM varia com a escolaridade, e estudos brasileiros mostraram que esse fator precisa ser considerado na interpretação. A comparação deve sempre usar uma referência compatível com a população avaliada.
        </p>
        <p className="text-[var(--text-muted)]">
          Em pessoas com alta escolaridade, alterações leves também podem ser menos evidentes em instrumentos breves. A comparação com outro instrumento está em{' '}
          <Link to="/mini-mental-ou-moca" className=" text-[var(--primary)] hover:underline">
            Mini Mental ou MoCA
          </Link>.
        </p>
      </section>
    </ArticleLayout>
  );
}
